import { deleteUser } from 'firebase/auth';
import { deleteDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { auth, collectionRef, docRef, studentSubCol, STUDENT_SUBCOLLECTIONS } from '../firebase/config';
import { COLLECTIONS, STUDENT_STATUS, USER_ROLE } from '../utils/constants';
import { normalizeEmail } from '../utils/emailUtils';
import { extractIdentityValues, releaseIdentities } from './identityUniqueness';

async function deleteQueryDocs(snap) {
  const results = await Promise.allSettled(snap.docs.map((d) => deleteDoc(d.ref)));
  const failed = results.filter((r) => r.status === 'rejected').length;
  return { total: snap.docs.length, failed };
}

async function deleteStudentSubcollections(studentId) {
  const summary = {};
  for (const sub of STUDENT_SUBCOLLECTIONS) {
    const snap = await getDocs(studentSubCol(studentId, sub));
    const { total, failed } = await deleteQueryDocs(snap);
    summary[sub] = { total, failed };
    if (failed > 0) {
      throw new Error(`Bazı kayıtlar silinemedi. Lütfen tekrar deneyin.`);
    }
  }
  return summary;
}

async function deleteNotificationsForUser(userId, userEmail) {
  const deleted = new Set();
  const queries = [
    query(collectionRef(COLLECTIONS.NOTIFICATIONS), where('userEmail', '==', userEmail)),
    query(collectionRef(COLLECTIONS.NOTIFICATIONS), where('recipientUid', '==', userId)),
  ];
  let failed = 0;
  for (const q of queries) {
    const snap = await getDocs(q);
    const refs = snap.docs.map((d) => d.ref).filter((ref) => {
      if (deleted.has(ref.path)) return false;
      deleted.add(ref.path);
      return true;
    });
    const results = await Promise.allSettled(refs.map((ref) => deleteDoc(ref)));
    failed += results.filter((r) => r.status === 'rejected').length;
  }
  return failed;
}

/**
 * Hesap silme — profil en son silinir; ara adım hata verirse öğrenci verisi korunur.
 */
export async function deleteAccountPermanently(loggedInUser) {
  const errors = [];
  const userEmail = normalizeEmail(loggedInUser.emailOrPhone);

  try {
    const consentsSnap = await getDocs(
      query(collectionRef(COLLECTIONS.CONSENTS), where('userId', '==', loggedInUser.id))
    );
    const { failed } = await deleteQueryDocs(consentsSnap);
    if (failed > 0) errors.push('Rıza kayıtları silinemedi.');
  } catch (e) {
    errors.push('Rıza kayıtları silinemedi.');
  }

  const fieldToMatch =
    loggedInUser.role === USER_ROLE.INSTRUCTOR ? 'instructorEmail' : 'studentEmail';

  try {
    const reqsSnap = await getDocs(
      query(collectionRef(COLLECTIONS.REQUESTS), where(fieldToMatch, '==', userEmail))
    );
    const { failed } = await deleteQueryDocs(reqsSnap);
    if (failed > 0) errors.push('Talepler silinemedi.');
  } catch (e) {
    errors.push('Talepler silinemedi.');
  }

  try {
    const failed = await deleteNotificationsForUser(loggedInUser.id, userEmail);
    if (failed > 0) errors.push('Bildirimler silinemedi.');
  } catch (e) {
    errors.push('Bildirimler silinemedi.');
  }

  if (loggedInUser.role === USER_ROLE.INSTRUCTOR) {
    try {
      const instructorUid = loggedInUser.id;
      const [studentsByEmailSnap, studentsByIdSnap] = await Promise.all([
        getDocs(
          query(
            collectionRef(COLLECTIONS.STUDENTS),
            where('instructorEmail', '==', userEmail)
          )
        ),
        getDocs(
          query(
            collectionRef(COLLECTIONS.STUDENTS),
            where('instructorId', '==', instructorUid)
          )
        ),
      ]);

      const studentRefs = new Map();
      [...studentsByEmailSnap.docs, ...studentsByIdSnap.docs].forEach((d) => {
        studentRefs.set(d.id, d.ref);
      });

      const results = await Promise.allSettled(
        [...studentRefs.values()].map((ref) =>
          updateDoc(ref, {
            instructorId: null,
            instructorEmail: null,
            status: STUDENT_STATUS.NEW,
          })
        )
      );
      if (results.some((r) => r.status === 'rejected')) {
        errors.push('Öğrenci bağlantıları güncellenemedi.');
      }

      const pendingSnap = await getDocs(
        query(
          collectionRef(COLLECTIONS.STUDENTS),
          where('pendingCoachInstructorEmail', '==', userEmail)
        )
      );
      const pendingResults = await Promise.allSettled(
        pendingSnap.docs.map((d) => updateDoc(d.ref, { pendingCoachInstructorEmail: null }))
      );
      if (pendingResults.some((r) => r.status === 'rejected')) {
        errors.push('Öğrenci bağlantıları güncellenemedi.');
      }
    } catch (e) {
      errors.push('Öğrenci bağlantıları güncellenemedi.');
    }
  }

  if (errors.length > 0) {
    throw new Error(
      'Hesap tam silinemedi. Profil korundu; lütfen tekrar deneyin veya destek ile iletişime geçin.'
    );
  }

  if (loggedInUser.role === USER_ROLE.STUDENT) {
    try {
      await deleteStudentSubcollections(loggedInUser.id);
    } catch (e) {
      throw new Error(e.message || 'Bazı kayıtlar silinemedi. Hesap silme iptal edildi.');
    }
  }

  const col =
    loggedInUser.role === USER_ROLE.INSTRUCTOR ? COLLECTIONS.INSTRUCTORS : COLLECTIONS.STUDENTS;

  try {
    await releaseIdentities(
      loggedInUser.id,
      extractIdentityValues(loggedInUser)
    );
  } catch {
    /* kimlik indeksi temizlenemezse profil silmeye devam */
  }

  await deleteDoc(docRef(col, loggedInUser.id));

  if (auth.currentUser) {
    try {
      await deleteUser(auth.currentUser);
    } catch (e) {
      return {
        errors: ['AUTH_DELETE_FAILED'],
        profileDeleted: true,
      };
    }
  }

  return { errors: [], profileDeleted: true };
}
